'use client'

import { ReactNode } from 'react'

import { Check } from 'lucide-react'

import { useFieldContext } from '@/components/form'
import {
  FieldWrapper,
  FieldWrapperClasses
} from '@/components/form/field-wrapper'

import { cn } from '@/utils'

export interface ButtonSelectorOption {
  icon?: ((selected: boolean) => ReactNode) | ReactNode
  label: string
  value: string
}

interface ButtonSelectorProps {
  classNames?: FieldWrapperClasses
  disabled?: boolean
  id?: string
  label?: string
  multiple?: boolean
  onChange?: (value: string | string[]) => void
  options: ButtonSelectorOption[]
  required?: boolean
  value?: string | string[]
}

export function ButtonSelector({
  classNames = {},
  disabled = false,
  id,
  label,
  multiple = false,
  onChange: onChangeProp,
  options,
  required = false,
  value: valueProp
}: ButtonSelectorProps) {
  const field = useFieldContext<string | string[]>()
  const value = valueProp ?? field.state.value
  const onChange = onChangeProp ?? field.handleChange

  const isSelected = (optionValue: string) => {
    if (multiple && Array.isArray(value)) {
      return value.includes(optionValue)
    }
    return value === optionValue
  }

  const handleClick = (optionValue: string) => {
    if (disabled) return
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : []
      if (currentValues.includes(optionValue)) {
        const newValues = currentValues.filter((v) => v !== optionValue)
        onChange(newValues)
      } else {
        onChange([...currentValues, optionValue])
      }
    } else {
      onChange(optionValue)
    }
  }

  return (
    <FieldWrapper
      classNames={classNames}
      field={field}
      id={id}
      label={label}
      required={required}
      role="group"
    >
      <div className="flex gap-2">
        {options.map((option) => {
          const selected = isSelected(option.value)
          return (
            <button
              aria-disabled={disabled || undefined}
              aria-pressed={selected}
              className={cn(
                'flex flex-1 items-center justify-between gap-3 rounded-lg border px-4 py-3 transition-colors',
                selected
                  ? 'border-foreground bg-background'
                  : 'border-border hover:border-muted-foreground',
                disabled && 'cursor-not-allowed opacity-60'
              )}
              key={option.value}
              onClick={() => handleClick(option.value)}
              type="button"
            >
              <span className="flex items-center gap-2 text-sm font-medium rtl:flex-row-reverse">
                {typeof option.icon === 'function'
                  ? option.icon(selected)
                  : option.icon}
                {option.label}
              </span>
              {selected && <Check className="text-foreground h-4 w-4" />}
            </button>
          )
        })}
      </div>
    </FieldWrapper>
  )
}
